// ============================================================================
// shared/ui/Modal.tsx — Modal sobre @radix-ui/react-dialog (reemplaza react-bootstrap
// Modal del Sidebar y MUI Modal de CambiarContrasenia). Radix exige un Dialog.Title:
// si no hay título visible se pone sr-only. Ver PLAN §B.3 / §B.6.
// API: open / onOpenChange (equivale a show+onHide / open+onClose de las libs previas).
//
// Props opcionales (retrocompatibles, no cambian a los consumidores existentes):
//   size        -> ancho del modal ("md" por defecto = comportamiento previo).
//   showClose    -> muestra la "X" de cierre arriba a la derecha (default false).
//   dismissable -> permite cerrar con Esc / click fuera (default true = Radix normal).
// Radix apila overlays de forma nativa: al abrir un modal sobre otro, el fondo se
// oscurece dos veces (requerimiento "modal que abre otro modal").
// ============================================================================
import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";

type ModalSize = "md" | "lg" | "xl" | "2xl";

interface ModalProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: string;
  children?: ReactNode;
  className?: string;
  size?: ModalSize;
  showClose?: boolean;
  dismissable?: boolean;
  // Fondo del overlay (opcional). Por defecto el oscurecido de siempre (negro 50%).
  overlayClassName?: string;
}

const SIZE_CLASS: Record<ModalSize, string> = {
  md: "tw-max-w-md",
  lg: "tw-max-w-2xl",
  xl: "tw-max-w-5xl",
  // Modales de trabajo amplios (p. ej. árbol de accesos sobre el formulario de usuario).
  "2xl": "tw-max-w-[1400px]",
};

// Animaciones (tailwindcss-animate). Solo opacidad (fade) para NO chocar con el
// transform de centrado (-translate-1/2). El "out" solo aplica cuando el modal se
// cierra por estado (Sidebar/CambiarContrasenia); en el panel el router desmonta.
const ANIM_OVERLAY =
  "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 " +
  "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 tw-duration-200";
const ANIM_CONTENT =
  "data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 " +
  "data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 tw-duration-200";

export const Modal = ({
  open,
  onOpenChange,
  title = "",
  children,
  className = "",
  size = "md",
  showClose = false,
  dismissable = true,
  overlayClassName = "tw-bg-black/50",
}: ModalProps) => (
  <Dialog.Root open={open} onOpenChange={onOpenChange}>
    <Dialog.Portal>
      <Dialog.Overlay className={`tw-fixed tw-inset-0 tw-z-[1050] ${overlayClassName} ${ANIM_OVERLAY}`} />
      <Dialog.Content
        // Cuando dismissable=false solo se cierra con la "X" o con el botón "Cancelar"
        // del propio contenido: se evita el cierre accidental por Esc / click fuera
        // (protege datos sin guardar en los formularios).
        onEscapeKeyDown={dismissable ? undefined : (e) => e.preventDefault()}
        onPointerDownOutside={dismissable ? undefined : (e) => e.preventDefault()}
        onInteractOutside={dismissable ? undefined : (e) => e.preventDefault()}
        // Responsive: se centra dentro de la ZONA DE LA PÁGINA (a la derecha del sidebar),
        // no del viewport, para NO quedar encima del sidebar al redimensionar. `--sidebar-w`
        // la publica el SideBar (fallback 0px si no hay sidebar, p.ej. login). El ancho llena
        // la zona menos un margen y lo limita `size` (SIZE_CLASS); alto máx 90vh con scroll.
        style={{
          left: "calc(50% + var(--sidebar-w, 0px) / 2)",
          width: "calc(100vw - var(--sidebar-w, 0px) - 2rem)",
        }}
        className={`tw-fixed tw-top-1/2 tw-z-[1055] -tw-translate-x-1/2 -tw-translate-y-1/2 ${SIZE_CLASS[size]} tw-max-h-[90vh] tw-overflow-y-auto tw-rounded-lg tw-bg-white tw-p-3 sm:tw-p-4 tw-shadow-xl focus:tw-outline-none ${ANIM_CONTENT} ${className}`}
      >
        <Dialog.Title className={title ? "tw-mb-2 tw-text-lg tw-font-semibold tw-pr-8" : "tw-sr-only"}>
          {title || "Diálogo"}
        </Dialog.Title>

        {showClose && (
          <button
            type="button"
            aria-label="Cerrar"
            onClick={() => onOpenChange?.(false)}
            className="tw-absolute tw-right-3 tw-top-3 tw-z-10 tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-text-gray-500 hover:tw-bg-gray-100 hover:tw-text-gray-800 focus:tw-outline-none"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="6" y1="6" x2="18" y2="18" />
              <line x1="18" y1="6" x2="6" y2="18" />
            </svg>
          </button>
        )}

        {children}
      </Dialog.Content>
    </Dialog.Portal>
  </Dialog.Root>
);
